import { useCallback, useEffect, useMemo, useState } from 'react';
import { IntlShape } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { updateAlertsCount, updateWellInfo } from '../../../redux/WellGuardian';
import {
  useCountryState,
  useSelectWellInfo
} from '../../../redux/WellGuardian/selectors';

// const WELL_LINK_TYPE = 'well';

// const SITE_LINK_TYPE = 'platform';

const inFlightWellInfoRequests = new Map<string, Promise<{ response: any; error?: any }>>();
const wellInfoResponseCache = new Map<string, any>();

export const useFetchWgHeaderData = (
  tuwi: string | undefined,
  intl: IntlShape,
  reloadAlertsCount?: boolean,
) => {
  const [wellInfoLoading, setWellInfoLoading] = useState<boolean>(false);
  const [alertsCountLoading, setAlertsCountLoading] = useState<boolean>(false);
  // const [r2SLinkLoading, setR2SLinkLoading] = useState<boolean>(false);

  const countryState = useCountryState();
  const wellInfo = useSelectWellInfo();
  // const site = useSelectedSite();
  const dispatch = useDispatch();

  /* istanbul ignore next */
  const countryKey = useMemo(() => JSON.stringify(countryState || {}), [countryState]);

  const fetchWellInfo = useCallback(async () => {
    {/* istanbul ignore next */}
    if (!tuwi) return;

    const key = `${tuwi}::${countryKey}`;
    if (wellInfoResponseCache.has(key)) {
      const cached = wellInfoResponseCache.get(key);
      dispatch(updateWellInfo(cached));
      return;
    }
    setWellInfoLoading(true);
    let request = inFlightWellInfoRequests.get(key);
    if (!request) {
      request = (async () => {
        try {
          const result = await wellGuardianApiClient.getWellInfoByTuwi(tuwi, countryState);
          if ((result as any).error == null) {
            wellInfoResponseCache.set(key, (result as any).response);
          }
          return result as { response: any; error?: any };
        } finally {
          inFlightWellInfoRequests.delete(key);
        }
      })();
      inFlightWellInfoRequests.set(key, request);
    }
    const { response, error } = await request;
    if (!error) {
      dispatch(updateWellInfo(response));
    /* istanbul ignore next */
    } else {
      toast.error(intl.formatMessage({ id: 'wellGuardian.header.errorLoadingWellInfo' }), {
        hideProgressBar: false,
        theme: 'colored',
      });
      setWellInfoLoading(false);
    }

    setWellInfoLoading(false);
  }, [tuwi, countryKey, dispatch, countryState, intl]);

  const fetchAlertsCount = useCallback(async () => {
    if (!tuwi) return; // cannot fetch alerts without tuwi
    setAlertsCountLoading(true);
    const { response, error } = await wellGuardianApiClient.getAlertsCountByTuwi(tuwi);
    if (!error) {
      dispatch(updateAlertsCount(response));
    }
    setAlertsCountLoading(false);
  }, [tuwi, dispatch]);

  // const fetchR2SLink = useCallback(async () => {
  //   setR2SLinkLoading(true);
  //   const r2SLink = tuwi
  //     ? await wellGuardianApiClient.getR2SLink(tuwi, WELL_LINK_TYPE)
  //     : await wellGuardianApiClient.getR2SLink(site, SITE_LINK_TYPE);
  //   dispatch(updateR2SLink(r2SLink ?? null));
  //   setR2SLinkLoading(false);
  // }, [tuwi, site, dispatch]);

  useEffect(() => {
    if (tuwi && wellInfo?.wellViewId !== tuwi && countryState) {
      fetchWellInfo();
      fetchAlertsCount();
      // fetchR2SLink();
    }
    // else if (!tuwi && countryState) {
    //   fetchR2SLink();
    // }
  }, [wellInfo, tuwi, fetchAlertsCount, fetchWellInfo, countryState]);

  useEffect(() => {
    if (reloadAlertsCount) {
      fetchAlertsCount();
    }
  }, [reloadAlertsCount, fetchAlertsCount]);

  return {
    alertsCountLoading,
    wellInfoLoading,
    wellInfo,
    // r2SLinkLoading,
  };
};
